import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { X, Plus } from "lucide-react";
import { ListingMediaSection } from "@/components/ListingMediaSection";

const emptyListing = {
  name_en: "", name_he: "",
  neighborhood_en: "", neighborhood_he: "",
  description_en: "", description_he: "",
  bedrooms: 1, bathrooms: 1, max_guests: 2,
  price_per_night: 200, cleaning_fee: 50, security_deposit: 300,
  currency: "USD",
  cover_image: "", images: [],
  house_rules_en: [], house_rules_he: [],
  amenities: [],
  latitude: 32.0853, longitude: 34.7818,
  price_overrides: {}, external_ical_urls: [],
};

const Field = ({ label, value, onChange, type = "text", testid, full }) => (
  <label className={`block ${full ? "sm:col-span-2" : ""}`}>
    <span className="text-xs uppercase tracking-wider text-[#8C7A6B]">{label}</span>
    <input
      data-testid={testid}
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className="input-line"
    />
  </label>
);

export const AdminListingSheet = ({ open, onOpenChange, listing, onSaved }) => {
  const { t } = useTranslation();
  const isEdit = !!listing?.listing_id;
  const [data, setData] = useState(listing || emptyListing);
  const [overrideDate, setOverrideDate] = useState("");
  const [overridePrice, setOverridePrice] = useState("");
  const [busy, setBusy] = useState(false);

  const upd = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const imagesAsArray = () =>
    Array.isArray(data.images)
      ? data.images
      : (data.images || "").split(",").map((s) => s.trim()).filter(Boolean);

  const setImages = (next) => upd("images", next);

  const [airbnbUrl, setAirbnbUrl] = useState("");
  const [airbnbHtml, setAirbnbHtml] = useState("");
  const [showHtmlPaste, setShowHtmlPaste] = useState(false);
  const [importing, setImporting] = useState(false);

  const applyImport = (d) => {
    setData((cur) => ({
      ...cur,
      name_en: d.name_en || cur.name_en,
      description_en: d.description_en || cur.description_en,
      neighborhood_en: d.neighborhood_en || cur.neighborhood_en,
      cover_image: d.cover_image || cur.cover_image,
      latitude: d.latitude ?? cur.latitude,
      longitude: d.longitude ?? cur.longitude,
    }));
    toast.success("Imported — review the fields before saving");
  };

  const importFromAirbnb = async () => {
    if (!airbnbUrl.trim()) { toast.error("Paste an Airbnb listing URL"); return; }
    setImporting(true);
    try {
      const r = await api.post("/admin/listings/import-airbnb", { url: airbnbUrl.trim() });
      applyImport(r.data);
    } catch (e) {
      const msg = e.response?.data?.detail || "Import failed";
      // If Airbnb blocked us, automatically reveal the paste-HTML fallback
      if (msg.toLowerCase().includes("rate") || msg.toLowerCase().includes("bot") || msg.toLowerCase().includes("view page source") || e.response?.status === 502) {
        setShowHtmlPaste(true);
      }
      toast.error(msg);
    } finally {
      setImporting(false);
    }
  };

  const importFromHtml = async () => {
    if (airbnbHtml.length < 200) { toast.error("Paste the full HTML source"); return; }
    setImporting(true);
    try {
      const r = await api.post("/admin/listings/import-airbnb-html", { html: airbnbHtml, url: airbnbUrl });
      applyImport(r.data);
      setAirbnbHtml("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not parse this HTML");
    } finally {
      setImporting(false);
    }
  };

  React.useEffect(() => {
    // Reset form data when a different listing is opened. Wrapped in a microtask to
    // satisfy react-hooks/set-state-in-effect (the parent also passes a key prop to remount).
    queueMicrotask(() => setData(listing || emptyListing));
  }, [listing]);

  const addOverride = () => {
    if (!overrideDate || !overridePrice) return;
    upd("price_overrides", { ...(data.price_overrides || {}), [overrideDate]: parseFloat(overridePrice) });
    setOverrideDate(""); setOverridePrice("");
  };
  const rmOverride = (d) => {
    const o = { ...(data.price_overrides || {}) };
    delete o[d];
    upd("price_overrides", o);
  };

  const save = async () => {
    // Required-field guard
    const requiredOk = ["name_en", "name_he", "neighborhood_en", "neighborhood_he", "cover_image"].every(
      (k) => (data[k] || "").toString().trim().length > 0
    );
    if (!requiredOk) { toast.error("Please fill the required fields"); return; }
    setBusy(true);
    try {
      const payload = {
        ...data,
        bedrooms: parseInt(data.bedrooms, 10),
        bathrooms: parseInt(data.bathrooms, 10),
        max_guests: parseInt(data.max_guests, 10),
        price_per_night: parseFloat(data.price_per_night),
        cleaning_fee: parseFloat(data.cleaning_fee || 0),
        security_deposit: parseFloat(data.security_deposit || 0),
        images: Array.isArray(data.images) ? data.images : (data.images || "").split(",").map((s) => s.trim()).filter(Boolean),
        house_rules_en: Array.isArray(data.house_rules_en) ? data.house_rules_en : (data.house_rules_en || "").split("\n").map((s) => s.trim()).filter(Boolean),
        house_rules_he: Array.isArray(data.house_rules_he) ? data.house_rules_he : (data.house_rules_he || "").split("\n").map((s) => s.trim()).filter(Boolean),
        external_ical_urls: Array.isArray(data.external_ical_urls) ? data.external_ical_urls : (data.external_ical_urls || "").split("\n").map((s) => s.trim()).filter(Boolean),
      };
      if (isEdit) {
        await api.patch(`/admin/listings/${listing.listing_id}`, payload);
      } else {
        await api.post("/admin/listings", payload);
      }
      toast.success("Saved");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const Field2 = null; void Field2; // placeholder — Field is now a module-level component

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto bg-[#FDFBF7]" data-testid="listing-sheet">
        <SheetHeader>
          <SheetTitle className="font-serif text-3xl text-[#2C2A29]">
            {isEdit ? t("admin.edit") : t("admin.add_listing")}
          </SheetTitle>
        </SheetHeader>

        <div className="mt-8 space-y-8">
          {!isEdit && (
            <section className="bg-[#F4EFE6] rounded-2xl p-5 border border-[#EAE3D9]" data-testid="airbnb-import-block">
              <h3 className="overline mb-2 flex items-center gap-2">Import from Airbnb <span className="text-[#C86D53]">(optional)</span></h3>
              <p className="text-xs text-[#8C7A6B] mb-3 leading-relaxed">
                Paste a public Airbnb URL (e.g. <code className="text-[#2C2A29]">airbnb.com/rooms/12345</code>). We&apos;ll prefill title, description, neighborhood and cover photo. You can still edit anything before saving.
              </p>
              <div className="flex gap-2">
                <input
                  type="url"
                  data-testid="airbnb-url-input"
                  value={airbnbUrl}
                  onChange={(e) => setAirbnbUrl(e.target.value)}
                  placeholder="https://www.airbnb.com/rooms/..."
                  className="input-line flex-1"
                />
                <button type="button" onClick={importFromAirbnb} disabled={importing}
                        data-testid="airbnb-import-btn"
                        className="btn-primary text-xs whitespace-nowrap disabled:opacity-50">
                  {importing ? "Fetching…" : "Import"}
                </button>
              </div>

              <div className="mt-3">
                <button type="button" onClick={() => setShowHtmlPaste((v) => !v)}
                        data-testid="airbnb-toggle-paste-btn"
                        className="text-xs text-[#8C7A6B] underline hover:text-[#C86D53]">
                  {showHtmlPaste ? "Hide paste-HTML fallback" : "Airbnb blocked us? Paste the page source instead →"}
                </button>
              </div>

              {showHtmlPaste && (
                <div className="mt-3" data-testid="airbnb-html-block">
                  <p className="text-xs text-[#595450] mb-2 leading-relaxed">
                    In your own browser, open the Airbnb listing, right-click anywhere and choose <strong>View page source</strong> (or press <code>Ctrl/⌘ + U</code>). Copy everything and paste it here:
                  </p>
                  <textarea
                    rows={6}
                    placeholder="Paste the full HTML source here…"
                    value={airbnbHtml}
                    onChange={(e) => setAirbnbHtml(e.target.value)}
                    data-testid="airbnb-html-input"
                    className="input-line resize-none w-full font-mono text-[11px]"
                  />
                  <button type="button" onClick={importFromHtml} disabled={importing}
                          data-testid="airbnb-import-html-btn"
                          className="btn-primary text-xs mt-3 disabled:opacity-50">
                    {importing ? "Parsing…" : "Parse pasted HTML"}
                  </button>
                </div>
              )}
            </section>
          )}

          <section>
            <h3 className="overline mb-3">Basics</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label={`${t("admin.name")} (EN)`} value={data.name_en} onChange={(v) => upd("name_en", v)} testid="name-en" />
              <Field label={`${t("admin.name")} (HE)`} value={data.name_he} onChange={(v) => upd("name_he", v)} testid="name-he" />
              <Field label={`${t("admin.neighborhood")} (EN)`} value={data.neighborhood_en} onChange={(v) => upd("neighborhood_en", v)} testid="neighborhood-en" />
              <Field label={`${t("admin.neighborhood")} (HE)`} value={data.neighborhood_he} onChange={(v) => upd("neighborhood_he", v)} testid="neighborhood-he" />
              <label className="block sm:col-span-2">
                <span className="text-xs uppercase tracking-wider text-[#8C7A6B]">{t("admin.description")} (EN)</span>
                <textarea data-testid="description-en" value={data.description_en} onChange={(e) => upd("description_en", e.target.value)} rows={4} className="input-line resize-none" />
              </label>
              <label className="block sm:col-span-2">
                <span className="text-xs uppercase tracking-wider text-[#8C7A6B]">{t("admin.description")} (HE)</span>
                <textarea data-testid="description-he" value={data.description_he} onChange={(e) => upd("description_he", e.target.value)} rows={4} className="input-line resize-none" />
              </label>
            </div>
          </section>

          <section>
            <h3 className="overline mb-3">Pricing & specs</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-5">
              <Field label="Bedrooms" value={data.bedrooms} onChange={(v) => upd("bedrooms", v)} type="number" testid="bedrooms" />
              <Field label="Bathrooms" value={data.bathrooms} onChange={(v) => upd("bathrooms", v)} type="number" testid="bathrooms" />
              <Field label="Max guests" value={data.max_guests} onChange={(v) => upd("max_guests", v)} type="number" testid="max-guests" />
              <Field label={t("admin.price")} value={data.price_per_night} onChange={(v) => upd("price_per_night", v)} type="number" testid="price" />
              <Field label={t("detail.cleaning")} value={data.cleaning_fee} onChange={(v) => upd("cleaning_fee", v)} type="number" testid="cleaning" />
              <Field label={t("detail.deposit")} value={data.security_deposit} onChange={(v) => upd("security_deposit", v)} type="number" testid="deposit" />
              <label className="block">
                <span className="text-xs uppercase tracking-wider text-[#8C7A6B]">Currency</span>
                <select
                  data-testid="currency-select"
                  value={data.currency || "USD"}
                  onChange={(e) => upd("currency", e.target.value)}
                  className="input-line"
                >
                  <option value="ILS">ILS (₪)</option>
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                </select>
              </label>
            </div>
          </section>

          <section>
            <h3 className="overline mb-3">Map location</h3>
            <div className="grid grid-cols-2 gap-5">
              <Field label="Latitude" value={data.latitude} onChange={(v) => upd("latitude", parseFloat(v) || 0)} type="number" testid="latitude" />
              <Field label="Longitude" value={data.longitude} onChange={(v) => upd("longitude", parseFloat(v) || 0)} type="number" testid="longitude" />
            </div>
            {data.latitude && data.longitude && (
              <div className="mt-4 rounded-xl overflow-hidden border border-[#EAE3D9]" style={{ height: 220 }}>
                <iframe
                  title="map-preview"
                  width="100%"
                  height="100%"
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${data.longitude-0.01}%2C${data.latitude-0.005}%2C${data.longitude+0.01}%2C${data.latitude+0.005}&layer=mapnik&marker=${data.latitude}%2C${data.longitude}`}
                />
              </div>
            )}
          </section>

          {isEdit && (
            <section data-testid="pricelabs-block">
              <h3 className="overline mb-3">Pricelabs sync <span className="text-[#C86D53]">(dynamic pricing)</span></h3>
              <p className="text-xs text-[#8C7A6B] mb-3 leading-relaxed">
                Paste the Pricelabs listing ID for this property, then click <strong>Save &amp; Sync now</strong>. Prices and min-stay rules land in <em>Per-date price overrides</em> below. Site prices are automatically reduced by the <strong>direct discount %</strong> (default 12%) vs Pricelabs.
              </p>
              <PricelabsIdPicker onPick={(id, pms) => { upd("pricelabs_listing_id", id); if (pms) upd("pricelabs_pms", pms); }} />
              <div className="grid grid-cols-3 gap-5 mt-3">
                <Field label="Pricelabs listing ID" value={data.pricelabs_listing_id} onChange={(v) => upd("pricelabs_listing_id", v)} testid="pricelabs-id" />
                <Field label="PMS (default: airbnb)" value={data.pricelabs_pms || "airbnb"} onChange={(v) => upd("pricelabs_pms", v)} testid="pricelabs-pms" />
                <Field label="Direct discount % (vs Pricelabs)" value={data.pricelabs_discount_percent ?? 12} onChange={(v) => upd("pricelabs_discount_percent", v)} type="number" testid="pricelabs-discount" />
              </div>
              <div className="flex items-center gap-3 mt-3 text-xs">
                <button type="button"
                        data-testid="pricelabs-sync-btn"
                        disabled={!listing?.listing_id || !data.pricelabs_listing_id}
                        onClick={async () => {
                          try {
                            // Save the pricelabs settings first so the backend can find them
                            await api.patch(`/admin/listings/${listing.listing_id}`, {
                              pricelabs_listing_id: data.pricelabs_listing_id,
                              pricelabs_pms: data.pricelabs_pms || "airbnb",
                              pricelabs_discount_percent: parseFloat(data.pricelabs_discount_percent ?? 12) || 0,
                            });
                            const r = await api.post(`/admin/listings/${listing.listing_id}/sync-pricelabs`);
                            toast.success(`${r.data.updated_prices || 0} prices · ${r.data.updated_min_stays || 0} min-stay rules imported`);
                            const fresh = await api.get(`/listings/${listing.listing_id}`);
                            upd("price_overrides", fresh.data.price_overrides || {});
                            upd("min_stay_overrides", fresh.data.min_stay_overrides || {});
                            upd("pricelabs_last_synced_at", fresh.data.pricelabs_last_synced_at);
                            upd("pricelabs_last_sync_error", null);
                            if (fresh.data.currency) upd("currency", fresh.data.currency);
                          } catch (e) {
                            toast.error(e.response?.data?.detail || "Sync failed");
                          }
                        }}
                        className="btn-primary text-xs disabled:opacity-50">
                  Save &amp; Sync now
                </button>
                {data.pricelabs_last_synced_at && (
                  <span className="text-[#8C7A6B]">
                    Last sync: {new Date(data.pricelabs_last_synced_at).toLocaleString()}
                  </span>
                )}
                {data.pricelabs_last_sync_error && (
                  <span className="text-[#C86D53]" title={data.pricelabs_last_sync_error}>⚠ {data.pricelabs_last_sync_error.slice(0,50)}</span>
                )}
              </div>
            </section>
          )}

          <ListingMediaSection
            data={data}
            setField={upd}
            setImages={setImages}
            imagesAsArray={imagesAsArray}
          />

          <section>
            <h3 className="overline mb-3">{t("detail.house_rules")}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <label className="block">
                <span className="text-xs uppercase tracking-wider text-[#8C7A6B]">{t("admin.rules_csv")} (EN)</span>
                <textarea data-testid="rules-en" value={Array.isArray(data.house_rules_en) ? data.house_rules_en.join("\n") : data.house_rules_en} onChange={(e) => upd("house_rules_en", e.target.value)} rows={4} className="input-line resize-none" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wider text-[#8C7A6B]">{t("admin.rules_csv")} (HE)</span>
                <textarea data-testid="rules-he" value={Array.isArray(data.house_rules_he) ? data.house_rules_he.join("\n") : data.house_rules_he} onChange={(e) => upd("house_rules_he", e.target.value)} rows={4} className="input-line resize-none" />
              </label>
            </div>
          </section>

          <section>
            <h3 className="overline mb-3">{t("admin.price_overrides")}</h3>
            <p className="text-xs text-[#8C7A6B] mb-3">{t("admin.price_overrides_hint")}</p>
            <div className="flex gap-2 mb-3">
              <input type="date" value={overrideDate} onChange={(e) => setOverrideDate(e.target.value)} data-testid="override-date" className="input-line" />
              <input type="number" placeholder="Price" value={overridePrice} onChange={(e) => setOverridePrice(e.target.value)} data-testid="override-price" className="input-line" />
              <button type="button" onClick={addOverride} data-testid="add-override-btn" className="btn-secondary px-4 py-2 text-sm whitespace-nowrap">
                <Plus className="h-3.5 w-3.5 inline" /> {t("admin.add_override")}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(data.price_overrides || {}).map(([d, p]) => (
                <span key={d} className="inline-flex items-center gap-1 bg-[#F4EFE6] text-[#2C2A29] text-sm rounded-full px-3 py-1">
                  {d}: {data.currency || "USD"} {p}
                  <button onClick={() => rmOverride(d)} className="text-[#8C7A6B] hover:text-[#C86D53]"><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          </section>

          <section>
            <h3 className="overline mb-3">{t("admin.ical_import")}</h3>
            <textarea
              data-testid="ical-urls"
              value={Array.isArray(data.external_ical_urls) ? data.external_ical_urls.join("\n") : data.external_ical_urls}
              onChange={(e) => upd("external_ical_urls", e.target.value)}
              rows={3}
              placeholder={t("admin.ical_paste")}
              className="input-line resize-none"
            />
          </section>

          <div className="flex gap-3 pt-4 sticky bottom-0 bg-[#FDFBF7] pb-4">
            <button onClick={save} disabled={busy} className="btn-primary flex-1" data-testid="save-listing-btn">
              {busy ? "..." : t("admin.save")}
            </button>
            <button onClick={() => onOpenChange(false)} className="btn-ghost" data-testid="cancel-listing-btn">{t("admin.cancel")}</button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

const PricelabsIdPicker = ({ onPick }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/pricelabs/listings");
      setItems(r.data || []);
      setOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not fetch Pricelabs listings");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="text-xs">
      <button type="button" onClick={load} disabled={busy}
              data-testid="pricelabs-load-listings-btn"
              className="underline text-[#C86D53] disabled:opacity-50">
        {busy ? "Loading…" : (items ? "Refresh my Pricelabs listings" : "Show my Pricelabs listings")}
      </button>
      {open && items && (
        <div className="mt-2 border border-[#EAE3D9] rounded-lg divide-y divide-[#EAE3D9] bg-white max-h-52 overflow-auto">
          {items.length === 0 && <div className="p-3 text-[#8C7A6B]">No Pricelabs listings found. Check PRICELABS_API_KEY.</div>}
          {items.map((it) => (
            <button type="button" key={it.id} onClick={() => { onPick(it.id, it.pms); setOpen(false); toast.success("ID applied — click Save & Sync"); }}
                    className="w-full text-left px-3 py-2 hover:bg-[#F4EFE6] flex justify-between gap-3"
                    data-testid={`pricelabs-pick-${it.id}`}>
              <div className="min-w-0">
                <div className="truncate font-medium text-[#2C2A29]">{it.name || it.id}</div>
                <div className="text-[10px] text-[#8C7A6B]">{it.city || "?"} · {it.bedrooms}br · {it.pms} · {it.push_enabled ? "sync ON" : "sync OFF"}</div>
              </div>
              <code className="text-[10px] text-[#8C7A6B] shrink-0 self-center">{it.id}</code>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
